class MakerCutterConfigurator extends HTMLElement {
  #photoSwipeLightbox;
  #photoSwipePromise;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#sync);
    this.addEventListener('input', this.#syncCharacterCounts);
    this.addEventListener('variant:update', this.#syncAfterVariantUpdate);

    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#sync);
    this.removeEventListener('input', this.#syncCharacterCounts);
    this.removeEventListener('variant:update', this.#syncAfterVariantUpdate);
    this.#photoSwipeLightbox?.destroy();
    this.#photoSwipeLightbox = undefined;
    this.#photoSwipePromise = undefined;
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const imageTrigger = event.target.closest('[data-maker-cutter-image-trigger]');
    if (imageTrigger instanceof HTMLAnchorElement) {
      if (
        event instanceof MouseEvent &&
        (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      void this.#openImage(imageTrigger);
      return;
    }

    const toggle = event.target.closest('[data-maker-cutter-toggle]');
    if (!(toggle instanceof HTMLButtonElement) || toggle.disabled) return;

    const inputId = toggle.getAttribute(toggle.getAttribute('aria-pressed') === 'true' ? 'data-off-input' : 'data-on-input');
    const input = inputId ? this.querySelector(`#${CSS.escape(inputId)}`) : null;

    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #openImage = async (trigger) => {
    const triggers = Array.from(this.querySelectorAll('[data-maker-cutter-image-trigger]')).filter(
      (item) => item instanceof HTMLAnchorElement
    );
    const index = triggers.indexOf(trigger);
    if (index < 0) return;

    try {
      const lightbox = await this.#getPhotoSwipe();
      lightbox.options.dataSource = triggers.map((item) => {
        const thumbnail = item.querySelector('img');
        return {
          src: item.href,
          msrc: thumbnail instanceof HTMLImageElement ? thumbnail.currentSrc || thumbnail.src : undefined,
          width: Number(item.dataset.imageWidth) || 1600,
          height: Number(item.dataset.imageHeight) || 1200,
          alt: thumbnail instanceof HTMLImageElement ? thumbnail.alt : item.getAttribute('aria-label') || '',
          thumbCropped: true,
        };
      });
      lightbox.loadAndOpen(index);
    } catch (error) {
      console.error('[Maker Cutter configurator] Unable to open image viewer.', error);
      window.location.assign(trigger.href);
    }
  };

  #getPhotoSwipe() {
    if (this.#photoSwipeLightbox) return Promise.resolve(this.#photoSwipeLightbox);
    if (this.#photoSwipePromise) return this.#photoSwipePromise;

    const lightboxUrl = this.dataset.photoswipeLightboxUrl;
    const moduleUrl = this.dataset.photoswipeModuleUrl;
    if (!lightboxUrl || !moduleUrl) return Promise.reject(new Error('PhotoSwipe asset URLs are missing.'));

    this.#photoSwipePromise = import(lightboxUrl).then(({ default: PhotoSwipeLightbox }) => {
      const lightbox = new PhotoSwipeLightbox({
        dataSource: [],
        pswpModule: () => import(moduleUrl),
        bgOpacity: 0.92,
      });
      lightbox.init();
      this.#photoSwipeLightbox = lightbox;
      return lightbox;
    });

    return this.#photoSwipePromise;
  }

  #syncAfterVariantUpdate = () => {
    requestAnimationFrame(this.#sync);
  };

  #sync = () => {
    this.#syncToggleButtons();
    this.#syncConditionalProperties();
    this.#syncCharacterCounts();
    this.#syncUpsellVariant();
  };

  #syncUpsellVariant() {
    const selectedVariant = this.querySelector('[data-maker-cutter-upsell-variant]:checked');
    if (!(selectedVariant instanceof HTMLInputElement)) return;

    const variantIdInput = this.querySelector('[data-maker-cutter-upsell-variant-id]');
    const addButton = this.querySelector('[data-maker-cutter-upsell-add]');
    const addLabel = this.querySelector('[data-maker-cutter-upsell-add-label]');
    const addSeparator = this.querySelector('[data-maker-cutter-upsell-add-separator]');
    const addPrice = this.querySelector('[data-maker-cutter-upsell-add-price]');
    const addButtonContainer = addButton?.closest('add-to-cart-component');
    const viewProductLink = this.querySelector('[data-maker-cutter-upsell-view-product]');
    const imageTrigger = this.querySelector('[data-maker-cutter-image-trigger]');
    const image = imageTrigger?.querySelector('img');
    const isAvailable = selectedVariant.dataset.variantAvailable === 'true';

    if (variantIdInput instanceof HTMLInputElement) {
      variantIdInput.value = selectedVariant.dataset.variantId || '';
    }

    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = !isAvailable;
    }

    if (addLabel instanceof HTMLElement) {
      addLabel.textContent = isAvailable ? this.dataset.addLabel || 'Add to cart' : this.dataset.soldOutLabel || 'Sold out';
    }

    if (addSeparator instanceof HTMLElement) {
      addSeparator.hidden = !isAvailable;
    }

    if (addPrice instanceof HTMLElement) {
      addPrice.hidden = !isAvailable;
      addPrice.textContent = selectedVariant.dataset.variantPrice || '';
    }

    if (addButtonContainer instanceof HTMLElement && selectedVariant.dataset.variantImageThumb) {
      addButtonContainer.dataset.productVariantMedia = selectedVariant.dataset.variantImageThumb;
    }

    if (viewProductLink instanceof HTMLAnchorElement && selectedVariant.dataset.variantUrl) {
      viewProductLink.href = selectedVariant.dataset.variantUrl;
    }

    if (imageTrigger instanceof HTMLAnchorElement && selectedVariant.dataset.variantImage) {
      imageTrigger.href = selectedVariant.dataset.variantImage;
      imageTrigger.dataset.imageWidth = selectedVariant.dataset.variantImageWidth || '1600';
      imageTrigger.dataset.imageHeight = selectedVariant.dataset.variantImageHeight || '1200';
    }

    if (image instanceof HTMLImageElement && selectedVariant.dataset.variantImageThumb) {
      image.src = selectedVariant.dataset.variantImageThumb;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
    }
  }

  #syncToggleButtons() {
    for (const toggle of this.querySelectorAll('[data-maker-cutter-toggle]')) {
      if (!(toggle instanceof HTMLButtonElement)) continue;

      const onInputId = toggle.dataset.onInput;
      const onInput = onInputId ? this.querySelector(`#${CSS.escape(onInputId)}`) : null;
      const isSelected = onInput instanceof HTMLInputElement && onInput.checked;

      toggle.setAttribute('aria-pressed', String(isSelected));
      toggle.classList.toggle('is-selected', isSelected);
    }
  }

  #syncConditionalProperties() {
    for (const row of this.querySelectorAll('.maker-cutter-configurator__variant--has-property')) {
      row.classList.remove('is-condition-active');
    }

    for (const property of this.querySelectorAll('[data-condition-option]')) {
      if (!(property instanceof HTMLElement)) continue;

      const optionName = property.dataset.conditionOption;
      const optionValue = property.dataset.conditionValue;
      if (!optionName || !optionValue) continue;

      const selectedOption = Array.from(this.querySelectorAll('[data-option-name]:checked')).find(
        (input) => input instanceof HTMLInputElement && input.dataset.optionName === optionName
      );
      const isActive = selectedOption instanceof HTMLInputElement && selectedOption.value === optionValue;
      const controllingRow = selectedOption?.closest('.maker-cutter-configurator__row');

      property.hidden = !isActive;
      property.classList.toggle('is-disabled', !isActive);
      property.setAttribute('aria-disabled', String(!isActive));
      if (isActive) controllingRow?.classList.add('is-condition-active');
      for (const input of property.querySelectorAll('input, textarea, select')) {
        if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement) {
          input.disabled = !isActive;
        }
      }
    }
  }

  #syncCharacterCounts = () => {
    for (const input of this.querySelectorAll('[data-maker-cutter-text-input]')) {
      if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) continue;

      const counter = input.parentElement?.querySelector('[data-maker-cutter-count]');
      if (!counter) continue;

      counter.textContent = `${input.value.length}/${input.maxLength}`;
    }
  };
}

if (!customElements.get('maker-cutter-configurator')) {
  customElements.define('maker-cutter-configurator', MakerCutterConfigurator);
}
